import { usePatchProgramBroadcasts } from '@/features/broadcast-schedule/api/patch-program-broadcast';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePatchClip } from '@/features/clips/api/patch-clips';
import { usePatchSegment } from '@/features/segments/api/patch-segment';
import { useMe } from '@/features/users/api/get-me';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { useReport } from '@/stores/report-store';
import { FormTypeEnum } from '@/types';

import { TReportDrawerForm } from '../types';

type TReportDrawerControllerReturn = {
  reportDrawer: ReturnType<
    typeof useDisclosureWithData<{ type: FormTypeEnum; data: TReportDrawerForm | null }>
  >;
  handleCreateReport: (data: TReportDrawerForm) => void;
  handleOnClose: () => void;
};

export function useReportDrawerController(): TReportDrawerControllerReturn {
  const reportDrawer = useDisclosureWithData<{
    type: FormTypeEnum;
    data: TReportDrawerForm | null;
  }>();
  const { setActiveClip, setActiveSegment, setSegmentItems, setClipItems } =
    useBroadcastScheduleProgram();
  const { data: me } = useMe();
  const { mutateAsync: updateSegment } = usePatchSegment();
  const { mutateAsync: updateProgramBroadcast } = usePatchProgramBroadcasts();
  const { mutateAsync: updateClip } = usePatchClip();
  const { reportedId, reportType, setReport, setReportedId, setReportEditData } = useReport();

  const handleOnClose = () => {
    reportDrawer.close();
    setReportedId('');
    setReport(false);
    setReportEditData(null);
  };

  const handleCreateReportTypeSegment = async (data: TReportDrawerForm) => {
    if (!reportedId) return;

    await updateSegment({
      id: reportedId,
      review_incidence_note: data.description,
      reviewed_by_user_id: me?.id,
      review_incidence_type: data.type,
      review_state: 5,
    })
      .then(() => {
        setActiveSegment(prev => {
          if (prev?.id && reportedId === prev.id) {
            return {
              ...prev,
              review_incidence_note: data.description,
              review_incidence_type: data.type,
              reviewed_by_user_id: me?.id,
              review_state: 5,
            };
          }
          return prev;
        });
        setSegmentItems(prev => {
          if (!prev) return prev;
          return prev.map(item => {
            if (item.id === reportedId) {
              return {
                ...item,
                review_incidence_note: data.description,
                review_incidence_type: data.type,
                reviewed_by_user_id: me?.id,
                review_state: 5,
              };
            }
            return item;
          });
        });
        handleOnClose();
      })
      .catch(error => {
        console.error(error);
      });
  };

  const handleCreateReportTypeProgram = async (data: TReportDrawerForm) => {
    if (!reportedId) return;

    await updateProgramBroadcast({
      id: reportedId,
      review_incidence_note: data.description,
      reviewed_by_user_id: me?.id,
      review_incidence_type: data.type,
      review_state: 5,
    })
      .then(() => {
        handleOnClose();
      })
      .catch(error => {
        console.error(error);
      });
  };

  const handleCreateReportTypeClip = async (data: TReportDrawerForm) => {
    if (!reportedId) return;

    await updateClip({
      id: reportedId,
      review_incidence_note: data.description,
      reviewed_by_user_id: me?.id,
      review_incidence_type: data.type,
      review_state: 5,
    })
      .then(() => {
        setActiveClip(prev => {
          if (prev?.id && reportedId === prev.id) {
            return {
              ...prev,
              review_state: 5,
              review_incidence_note: data.description,
              review_incidence_type: data.type,
              reviewed_by_user_id: me?.id,
            };
          }
          return null;
        });
        setClipItems(prev => {
          if (!prev) return prev;
          return prev.map(item => {
            if (item.id === reportedId) {
              return {
                ...item,
                review_state: 5,
                review_incidence_note: data.description,
                review_incidence_type: data.type,
                reviewed_by_user_id: me?.id,
              };
            }
            return item;
          });
        });
        handleOnClose();
      })
      .catch(error => {
        console.error(error);
      });
  };

  const handleCreateReport = (data: TReportDrawerForm) => {
    if (!data || !reportedId) return;

    if (reportType === 'segment') {
      handleCreateReportTypeSegment(data);
    }

    if (reportType === 'program') {
      handleCreateReportTypeProgram(data);
    }

    if (reportType === 'clip') {
      handleCreateReportTypeClip(data);
    }
  };

  return {
    reportDrawer,
    handleCreateReport,
    handleOnClose,
  };
}
